// @flow

/* eslint-disable import/no-unresolved */
import './Map.css'
import mapboxgl from 'mapboxgl'
import greatCircle from '@turf/great-circle'
// import '../../../static/back-pattern.png'

export default class Map {
  constructor (mapEl, chapters: Array<any>) {
    this.mapReady = false
    this.addedPoints = []
    this.startTime = 0
    this.progress = 0
    this.framesPerSecond = 15
    this.initialOpacity = 1
    this.opacity = this.initialOpacity
    this.initialRadius = 12
    this.radius = this.initialRadius
    this.maxRadius = 25
    this.firstSymbolId = null
    this.activeIndex = 0
    this.chapters = chapters
    this.lineNames = []
    this.animatedLineNames = []
    this.polygonNames = []
    this.multiPolygonNames = []
    this.labelNames = []
    this.mapEl = mapEl
    this.animationLine = {
      'type': 'Feature',
      'geometry': {
        'type': 'LineString',
        'coordinates': []
      }
    }
    this.animationProgress = 0
    this.currentLine = null
    this.currentLineId = null
    this.lineGeometries = []
    // this.adaptChapterConfigBounds(this.chapters[0])

    mapboxgl.accessToken = 'pk.eyJ1IjoibnJrIiwiYSI6Ii14T3NfSzgifQ.0N5h3SI2b6bemxP_1YESIA'
    this.map = new mapboxgl.Map({
      ...this.chapters[this.activeIndex],
      container: mapEl,
      // style: 'mapbox://styles/nrk/cjda0mdyv8mwk2spdhnfk8upp',
      // style: 'mapbox://styles/nrk/cj07whxya001b2rr3t3qn875e',
      // style: 'mapbox://styles/nrk/cjd5nkoti4kph2spd13z34tmd', // BLå
      // style: 'mapbox://styles/nrk/cizjr6t96006v2smn4qp3l430', // NRK blue with satellite shading
      // style: 'mapbox://styles/nrk/cj07whxya001b2rr3t3qn875e', // Flybilder
      style: 'mapbox://styles/nrk/cjoicstuv10bd2ss5maaqzoaw', // Innafor - flybilder
      interactive: false,
      renderWorldCopies: false,
      attributionControl: false

    })

    this.map.on('load', () => {
      this.map.addLayer({
        id: 'raster-layer',
        type: 'raster',
        source: {
          type: 'raster',
          tiles: ['https://api.mapbox.com/v4/nrk.6gen9l2a/{z}/{x}/{y}.png?access_token=pk.eyJ1IjoibnJrIiwiYSI6Ii14T3NfSzgifQ.0N5h3SI2b6bemxP_1YESIA']
        },
        minzoom: 12,
        maxzoom: 22
      })
      // const layers = this.map.getStyle().layers
      // Find the index of the first symbol layer in the map style
      this.firstSymbolId = 2 // layers.find(layer => layer.type === 'symbol').id
      // this.map.addControl(new mapboxgl.ScaleControl())
      this.animateMarker(0)
      this.mapReady = true
      this.setActiveChapter(this.activeIndex, 0)
      // this.animationLoop(0)
    })
  }

  adaptChapterConfigBounds (chapterConfig) {
    const camera = this.map.cameraForBounds(chapterConfig.bounds)
    chapterConfig.zoom = camera.zoom
    chapterConfig.center = [camera.center.lng, camera.center.lat]
  }

  addChapter (chapter) {
    if (chapter.point) {
      for (let i = 0; i < this.addedPoints.length; i++) {
        if (this.map.getLayer(this.addedPoints[i]) !== undefined) {
          this.map.setPaintProperty(this.addedPoints[i], 'circle-opacity', 0)
        }
      }
      this.addPoint(chapter.point, chapter.id)
    }

    if (chapter.line) {
      this.addLine(chapter.line, chapter.id)
    }

    if (chapter.greatCircle) {
      this.addGreatCircle(chapter.greatCircle, chapter.mapColor, chapter.id)
    } else {
      this.currentLineId = null
    }

    if (chapter.polygon) {
      this.addPolygon(chapter.polygon, chapter.mapColor, chapter.id)
    }

    if (chapter.multiPolygon) {
      this.addMultiPolygon(chapter.multiPolygon, chapter.mapColor, chapter.id)
    }

    if (chapter.label) {
      this.addLabel(chapter.label, chapter.id)
    }

    if (chapter.labels) {
      chapter.labels.forEach((label, idx) => {
        this.addLabel(label, chapter.id + '_' + idx)
      })
    }
  }

  removeChapter (chapter) {
    if (chapter.point) {
      this.removePoint(chapter.id)
    }

    if (chapter.line) {
      this.removeLine(chapter.id)
    }

    if (chapter.greatCircle) {
      this.removeGreatCircle(chapter.id)
    }
  }

  fadeOutChapter (chapter) {
    if (chapter.polygon) {
      const polygonId = `polygon-${chapter.id}`
      if (this.map.getLayer(polygonId) !== undefined) {
        this.map.setPaintProperty(polygonId, 'fill-opacity', 0.5)
      }
    }
    if (chapter.multiPolygon) {
      const multiPolygonId = `multiPolygon-${chapter.id}`
      if (this.map.getLayer(multiPolygonId) !== undefined) {
        this.map.setPaintProperty(multiPolygonId, 'fill-opacity', 0.5)
      }
    }
  }

  fadeInChapter (chapter) {
    if (chapter.polygon) {
      const polygonId = `polygon-${chapter.id}`
      if (this.map.getLayer(polygonId) !== undefined) {
        this.map.setPaintProperty(polygonId, 'fill-opacity', 1)
      }
    }
    if (chapter.multiPolygon) {
      const multiPolygonId = `multiPolygon-${chapter.id}`
      if (this.map.getLayer(multiPolygonId) !== undefined) {
        this.map.setPaintProperty(multiPolygonId, 'fill-opacity', 1)
      }
    }
  }

  setActiveChapter (chapterIndex: number, duration) {
    this.activeIndex = chapterIndex
    if (!this.mapReady) {
      return
    }

    this.chapters.forEach((chapter, idx) => {
      if (idx < chapterIndex) {
        this.addChapter(chapter)
        this.fadeOutChapter(chapter)
      } else if (idx === chapterIndex) {
        this.addChapter(chapter)
        this.fadeInChapter(chapter)
      } else {
        this.removeChapter(chapter)
        this.fadeOutChapter(chapter)
      }
    })

    const activeChapter = this.chapters[chapterIndex]
    if (activeChapter.bounds) {
      this.adaptChapterConfigBounds(activeChapter)
    }

    if (duration === 0) {
      this.map.jumpTo(activeChapter)
    } else {
      const data = (activeChapter.polygon || {}).data || (activeChapter.multiPolygon || {}).data
      if (data && data.bbox) {
        this.map.fitBounds(data.bbox, {
          padding: 20,
          animate: true,
          duration: 2000
        })
      } else {
        this.map.flyTo(activeChapter)
      }
    }

    for (var i = 0; i < this.animatedLineNames.length; i++) {
      if (this.animatedLineNames[i] !== this.currentLineId) {
        this.map.getSource(this.animatedLineNames[i]).setData(this.lineGeometries[i])
      }
    }
  }

  animateMarker (timestamp) {
    setTimeout(() => {
      requestAnimationFrame(this.animateMarker.bind(this))
      let opacity = this.map.getZoom() + 1 - 15
      if (opacity < 0) {
        opacity = 0
      } else if (opacity > 1) {
        opacity = 1
      }

      this.map.setPaintProperty('raster-layer', 'raster-opacity', opacity)

      this.radius += (this.maxRadius - this.radius) / this.framesPerSecond
      this.opacity -= (0.9 / this.framesPerSecond)

      if (this.opacity <= 0) {
        this.radius = this.initialRadius
        this.opacity = this.initialOpacity
      }

      if (this.activeIndex === 7) {
        for (var i = 0; i < this.addedPoints.length; i++) {
          if (this.map.getLayer(this.addedPoints[i]) !== undefined) {
            this.map.setPaintProperty(this.addedPoints[i], 'circle-radius', this.radius)
            this.map.setPaintProperty(this.addedPoints[i], 'circle-opacity', this.opacity)
          }
        }
      } else {
        const currentPointName = `point-${this.chapters[this.activeIndex].id}`
        if (this.map.getLayer(currentPointName) !== undefined) {
          this.map.setPaintProperty(currentPointName, 'circle-radius', this.radius)
          this.map.setPaintProperty(currentPointName, 'circle-opacity', this.opacity)
        }
      }

      if (this.currentLine != null && this.currentLineId != null && this.animationProgress < this.currentLine.geometry.coordinates.length) {
        this.animationLine.geometry.coordinates.push(this.currentLine.geometry.coordinates[this.animationProgress])
        this.map.getSource(this.currentLineId).setData(this.animationLine)
        this.animationProgress++
      }
    }, 1000 / this.framesPerSecond)
  }

  toPaint (mapColor) {
    switch (mapColor) {
      case 'dark':
        return {
          'fill-color': '#2f0101',
          'fill-opacity': 0.9,
          'fill-outline-color': '#2f0101'
        }
      case 'pattern':
        return {
          'fill-pattern': 'tiny-checkers',
          'fill-outline-color': '#2f0101'
        }
      default:
        return {
          'fill-color': '#E1192A',
          'fill-opacity': 0.9,
          'fill-outline-color': '#2f0101'
        }
    }
  }

  addLabel (label, id) {
    const labelId = `label-${id}`
    if (this.labelNames.indexOf(labelId) === -1) {
      this.map.addLayer({
        'id': labelId,
        'type': 'symbol',
        'source': {
          'type': 'geojson',
          'data': label.data
        },
        'layout': {
          'symbol-placement': 'point',
          'text-font': ['Open Sans Regular'],
          'text-field': label.data.properties.name,
          'text-size': 18
        },
        'paint': {
          'text-color': '#111',
          'text-halo-color': '#fff',
          'text-halo-width': 1
        }
      }, this.firstSymbolId)
      this.labelNames.push(labelId)
    }
  }

  addLine (line, id) {
    const lineId = `line-${id}`
    if (this.lineNames.indexOf(lineId) === -1) {
      this.map.addLayer({
        'id': lineId,
        'type': 'line',
        'source': {
          'type': 'geojson',
          'data': line.data
        },
        'layout': {
          'line-cap': 'round',
          'line-join': 'round'
        },
        'paint': {
          'line-color': '#bb1414',
          'line-width': 5,
          'line-opacity': 0.6
        }
      }, this.firstSymbolId)

      this.lineNames.push(lineId)
    }
  }

  addPolygon (polygon, mapColor, id) {
    const polygonId = `polygon-${id}`
    if (this.polygonNames.indexOf(polygonId) === -1) {
      this.map.addLayer({
        'id': polygonId,
        'type': 'fill',
        'source': {
          'type': 'geojson',
          'data': polygon.data
        },
        'paint': this.toPaint(mapColor),
        'layout': {
        }
      }, this.firstSymbolId)
      this.polygonNames.push(polygonId)
    }
  }

  addMultiPolygon (multiPolygon, mapColor, id) {
    const multiPolygonId = `multiPolygon-${id}`
    if (this.multiPolygonNames.indexOf(multiPolygonId) === -1) {
      this.map.addLayer({
        'id': multiPolygonId,
        'type': 'fill',
        'source': {
          'type': 'geojson',
          'data': multiPolygon.data
        },
        'paint': this.toPaint(mapColor),
        'layout': {
        }
      }, this.firstSymbolId)
      this.multiPolygonNames.push(multiPolygonId)
    }
  }

  addPoint (point, id) {
    const pointId = `point-${id}`
    if (this.addedPoints.indexOf(pointId) === -1) {
      this.map.addSource(pointId, {
        'type': 'geojson',
        'data': {
          'type': 'Point',
          'coordinates': [
            point.lon, point.lat
          ]
        }
      })

      this.map.addLayer({
        'id': pointId,
        'source': pointId,
        'type': 'circle',
        'paint': {
          'circle-radius': this.initialRadius,
          'circle-radius-transition': { duration: 0 },
          'circle-opacity-transition': { duration: 0 },
          'circle-color': '#dd0000'
        }
      })

      this.map.addLayer({
        'id': pointId + '_base',
        'source': pointId,
        'type': 'circle',
        'paint': {
          'circle-radius': 10,
          'circle-color': '#aa0000',
          'circle-opacity': 0.7
        }
      })

      this.addedPoints.push(pointId)
    }
  }

  removeLine (id) {
    const lineId = `line-${id}`
    if (this.map.getLayer(lineId) !== undefined) {
      this.map.removeLayer(lineId)
      this.map.removeSource(lineId)

      this.lineNames.splice(this.lineNames.indexOf(lineId), 1)
    }
  }

  removePoint (id) {
    const pointId = `point-${id}`
    if (this.map.getLayer(pointId) !== undefined) {
      this.map.removeLayer(pointId)
      this.map.removeLayer(pointId + '_base')
      this.map.removeSource(pointId)

      this.addedPoints.splice(this.addedPoints.indexOf(pointId), 1)
    }
  }

  addGreatCircle (startStop, mapColor, id) {
    // const start = turf.point([-122, 48])
    // const end = turf.point([-77, 39])

    this.animationLine.geometry.coordinates = []
    this.animationProgress = 0
    this.currentLine = greatCircle(startStop.start, startStop.stop, { 'name': 'Oslo til New York' })

    const lineId = `line-${id}`
    this.currentLineId = lineId
    if (this.animatedLineNames.indexOf(lineId) === -1) {
      this.lineGeometries.push(this.currentLine)

      this.map.addLayer({
        'id': lineId,
        'type': 'line',
        'source': {
          'type': 'geojson',
          'data': this.animationLine
        },
        'layout': {
          'line-cap': 'round',
          'line-join': 'round'
        },
        'paint': {
          'line-color': '#bb1414',
          'line-width': 5,
          'line-opacity': 0.6,
          'line-dasharray': [2, 2]
        }
      }, this.firstSymbolId)

      this.animatedLineNames.push(lineId)
    }
  }

  removeGreatCircle (id) {
    const lineId = `line-${id}`
    if (this.map.getLayer(lineId) !== undefined) {
      this.map.removeLayer(lineId)
      // this.map.removeLayer(lineId + '_base')
      this.map.removeSource(lineId)

      const index = this.animatedLineNames.indexOf(lineId)
      this.animatedLineNames.splice(index, 1)
      this.lineGeometries.splice(index, 1)
    }
  }
}



// WEBPACK FOOTER //
// ./src/v1/components/Map.js